import { useState, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { Button } from './components/ui/button';
import { SettingsPage } from './components/SettingsPage';
import { TeamPage } from './components/TeamPage';
import { DevModePage } from './components/DevModePage';
import { TopNavBar } from './components/TopNavBar';
import { CoworkPage } from './cowork/components/CoworkPage';
import {
  parseAppPath,
  getPathForPage,
  type CurrentPage,
} from '@/routes';

export type Song = {
  id?: string;
  coverUrl: string;
  title: string;
  artist: string;
  duration: number;
  bpm: number;
  tempo?: number;
  mood: string;
  genre?: string;
  tags: string[];
  featured?: boolean;
  audioUrl?: string;
};

export default function App() {
  const userName = "Noemi Miki";
  const location = useLocation();
  const navigate = useNavigate();

  const routeParams = useMemo(() => {
    return parseAppPath(location.pathname);
  }, [location.pathname]);

  const { activeMode, currentPage } = routeParams;

  const [_mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleNavigate = useCallback(
    (page: string) => {
      const p = page as CurrentPage;
      navigate(getPathForPage(p));
    },
    [navigate]
  );

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Top Navigation Bar - full width, always on top */}
      <div className="hidden lg:block fixed top-0 left-0 right-0 z-50">
        <TopNavBar
          currentPage={currentPage}
          onPageChange={handleNavigate}
        />
      </div>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b z-50 flex items-center justify-between px-4">
        <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(true)}>
          <Menu className="w-6 h-6" />
        </Button>
        <img src="https://images.unsplash.com/photo-1653341899122-5a2bda10c094?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=100" alt="Profile" className="w-8 h-8 rounded-full" />
      </div>

      {/* Below top bar: content */}
      <div className="flex flex-1 min-h-0 pt-16 lg:pt-14">
        {activeMode === 'chat' ? (
          /* Legacy /chat deep links land in the unified shell on the Chats view */
          <div className="flex-1">
            <CoworkPage key="cowork-mode" initialView="chats" />
          </div>
        ) : activeMode === 'cowork' ? (
          <div className="flex-1">
            <CoworkPage key="cowork-mode" />
          </div>
        ) : currentPage === 'settings' ? (
          <div className="flex-1">
            <SettingsPage userName={userName} />
          </div>
        ) : currentPage === 'team' ? (
          <div className="flex-1">
            <TeamPage />
          </div>
        ) : currentPage === 'dev-mode' ? (
          <div className="flex-1">
            <DevModePage />
          </div>
        ) : (
          <div className="flex-1">
            <CoworkPage key="cowork-fallback" />
          </div>
        )}
      </div>
    </div>
  );
}
